import { bucketsInRange, yearBuckets, type ChartEvent, type ChartMarker } from '@finsim/charts';
import { timeAxis, type Project, type Scenario } from '@finsim/model';
import { useUnit } from 'effector-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { $project } from '../../../entities/project';
import { $focusedScenarioId, $selectedItem } from '../../../entities/selection';
import {
  $mcResult,
  $mcRunning,
  $result,
  type McScenarioResult,
  type MonteCarloResult,
  type ScenarioResult,
  type SimulationResult,
} from '../../../entities/simulation';
import {
  $hiddenScenarios,
  $simMode,
  $timeRange,
  $valueMode,
  type SimMode,
  type ValueMode,
} from '../../../entities/view-settings';
import { formatYear, makeFormatters, type Formatters } from '../../../shared/lib/format';
import { useLocale } from '../../../shared/lib/i18n/use-locale';
import { keyDateMarkers } from '../lib/markers';

export interface CanvasData {
  project: Project;
  result: SimulationResult;
  dates: Date[];
  valueMode: ValueMode;
  /** Visible [from, to] month indexes. */
  range: readonly [number, number];
  buckets: ReturnType<typeof yearBuckets>;
  /** Variants with results, in project order; `visible` excludes legend-hidden ones. */
  scenarios: Scenario[];
  visible: Scenario[];
  focused: Scenario | undefined;
  focusedResult: ScenarioResult | undefined;
  simMode: SimMode;
  /** Monte Carlo result in Monte Carlo mode (null until the first run finishes). */
  mc: MonteCarloResult | null;
  focusedMc: McScenarioResult | undefined;
  /** A Monte Carlo run is in progress (the shown result may be outdated). */
  mcPending: boolean;
  selectedItemId: string | null;
  fmt: Formatters;
  /** Axis markers for the focused variant. */
  events: ChartEvent[];
  /** The project's key dates, drawn on every time chart. */
  markers: ChartMarker[];
  /** "in 2026 money" when showing real values. */
  moneyCaption: string | undefined;
}

export function useCanvasData(): CanvasData | null {
  const { t } = useTranslation();
  const locale = useLocale();
  const [
    project,
    result,
    valueMode,
    focusedId,
    hidden,
    range,
    selected,
    simMode,
    mcResult,
    mcRunning,
  ] = useUnit([
    $project,
    $result,
    $valueMode,
    $focusedScenarioId,
    $hiddenScenarios,
    $timeRange,
    $selectedItem,
    $simMode,
    $mcResult,
    $mcRunning,
  ]);

  const dates = useMemo(
    () => (result ? timeAxis(result.startYearMonth, result.months) : []),
    [result],
  );
  const allBuckets = useMemo(() => yearBuckets(dates), [dates]);

  return useMemo(() => {
    if (!project || !result || project.scenarios.length === 0) return null;
    const last = result.months - 1;
    const r: readonly [number, number] = range && range[1] <= last ? range : [0, last];
    const scenarios = project.scenarios.filter((s) => result.scenarios[s.id] !== undefined);
    const visible = scenarios.filter((s) => !hidden.has(s.id));
    const focused = scenarios.find((s) => s.id === focusedId) ?? scenarios[0];
    const focusedResult = focused ? result.scenarios[focused.id] : undefined;
    const currency = project.assumptions.currency;
    // A result for another horizon (before a rerun) can't share the time axis.
    const mc = simMode === 'monteCarlo' && mcResult?.months === result.months ? mcResult : null;

    const names = new Map(Object.values(project.items).map((i) => [i.id as string, i.name]));
    const events: ChartEvent[] = (focusedResult?.events ?? []).flatMap((e): ChartEvent[] => {
      const name = e.itemId ? (names.get(e.itemId) ?? '') : '';
      switch (e.kind) {
        case 'oneOff':
          return [{ index: e.month, label: name }];
        case 'loanPaidOff':
          return [
            { index: e.month, label: t('charts.events.loanPaidOff', { name }), tone: 'positive' },
          ];
        case 'firstNegativeCash':
          return [
            { index: e.month, label: t('charts.events.firstNegativeCash'), tone: 'critical' },
          ];
        case 'assetLiquidated':
          return [{ index: e.month, label: t('charts.events.assetLiquidated', { name }) }];
        default:
          return [];
      }
    });

    return {
      project,
      result,
      dates,
      valueMode,
      range: r,
      buckets: bucketsInRange(allBuckets, r),
      scenarios,
      visible,
      focused,
      focusedResult,
      simMode,
      mc,
      focusedMc: focused && mc ? mc.scenarios[focused.id] : undefined,
      mcPending: simMode === 'monteCarlo' && mcRunning,
      selectedItemId: selected?.itemId ?? null,
      fmt: makeFormatters(locale, currency),
      events,
      markers: keyDateMarkers(project),
      moneyCaption:
        valueMode === 'real' && dates[0]
          ? t('charts.inMoney', { year: formatYear(dates[0], locale) })
          : undefined,
    };
  }, [
    project,
    result,
    valueMode,
    focusedId,
    hidden,
    range,
    selected,
    simMode,
    mcResult,
    mcRunning,
    dates,
    allBuckets,
    locale,
    t,
  ]);
}

/** Accessible one-line summary: "Rent: €80k → €1.4m; Buy: …" over the visible range. */
export function summarize(
  title: string,
  series: readonly { label: string; values: ArrayLike<number> }[],
  range: readonly [number, number],
  format: (v: number) => string,
): string {
  const parts = series.map(
    (s) => `${s.label}: ${format(s.values[range[0]] ?? 0)} → ${format(s.values[range[1]] ?? 0)}`,
  );
  return `${title}. ${parts.join('; ')}`;
}
